"use client";

/**
 * Detailed side-by-side comparison table.
 *
 * Rows grouped under collapsible sections (Price & Valuation, Property
 * Specs, Location, Market Context, Red Flags). Each row renders one
 * cell per listing, colored indirectly by the column header badge
 * (not the cell) - cell coloring would turn the table into a rainbow
 * and compete with the best/worst dots.
 *
 * Best-in-row: small green dot next to the winning cell.
 * Worst-in-row on normative metrics (overpriced_pct, red_flag_count):
 *   small red dot. For non-normative numerics (area, metro distance)
 *   we skip the worst dot - "smaller area" isn't objectively worse.
 *
 * On mobile we render the table as a vertically scrolling stack of
 * section accordions with horizontal overflow inside each table so
 * the data stays readable without a cramped squeeze.
 */
import { signedPct } from "@/lib/format/change";
import { useState } from "react";
import { useT, useLocale } from "@/lib/locale-context";
import { formatPrice } from "@/lib/api";
import { roundEstimate } from "@/lib/estimate";
import type { AnalysisResult } from "@/types";
import { translateVerdict, type TranslationKey } from "@/lib/i18n";
import { colorForIndex } from "./palette";
import { inOwnWords } from "@/lib/deed-claim";
import { bestAndWorstIndex, pricePerM2, redFlagCount } from "./metrics";
import { resolveBand, formatBand, ownComplexDelta } from "./confidence";
import { ChevronDown, Columns3, AlertTriangle, CheckCircle2 } from "lucide-react";

interface Props {
  results: AnalysisResult[];
}

// Utility: render a number if present, "-" otherwise.
function fmtNum(n: number | null | undefined, unit = "", digits = 0): string {
  if (n == null || !Number.isFinite(n)) return "-";
  const rounded = digits > 0 ? n.toFixed(digits) : Math.round(n).toLocaleString();
  return unit ? `${rounded} ${unit}` : rounded;
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  return signedPct(n, 1);
}

function fmtKm(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  return `${n.toFixed(1)} km`;
}

// Pull a typed value off `computed_features` without forcing a full
// typing for that dict - it's a loose ML-feature bag and varies over
// time.
function cf(r: AnalysisResult, key: string): number | null {
  const features = (r as unknown as { computed_features?: Record<string, number> })
    .computed_features;
  const v = features?.[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

interface RowSpec {
  labelKey: TranslationKey;
  values: Array<number | null | undefined>;   // numeric series (for dots)
  display: React.ReactNode[];                  // per-listing display (string or JSX for multi-line cells)
  lowerIsBetter: boolean;                      // for best/worst highlighting
  showWorst?: boolean;                         // skip red dot for non-normative metrics
  showBest?: boolean;                          // skip green dot for informational rows (e.g. asking price, fair value - ranking belongs on overpriced % / savings)
}

// Yes/No cell - returns no numeric values so no dots are rendered.
interface BoolRowSpec {
  labelKey: TranslationKey;
  values: Array<boolean | null | undefined>;
}

/** True when a rendered cell carries no information ("-" / localized N/A). */
function isEmptyCell(d: React.ReactNode, na?: string): boolean {
  return d == null || d === "-" || d === "" || (na !== undefined && d === na);
}

function NumericRow({ spec, t }: { spec: RowSpec; t: ReturnType<typeof useT> }) {
  // A row where EVERY listing has no data is dead weight - a wall of
  // "-" cells reads as "data missing" rather than "not applicable".
  if (spec.display.every((d) => isEmptyCell(d, t("compare.table.na")))) return null;
  const { best, worst } = bestAndWorstIndex(
    spec.values.map((v) => (typeof v === "number" ? v : null)),
    spec.lowerIsBetter,
  );
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <th scope="row" className="sticky left-0 z-10 bg-white text-xs font-medium text-gray-500 text-left py-3 pl-4 pr-4 whitespace-nowrap align-top">
        {t(spec.labelKey)}
      </th>
      {spec.display.map((d, i): React.ReactNode => {
        // Green = best, rose = worst per standard deal/loss semantics.
        // (Green is reserved for winners here - the Elan 1/2/3/4 card
        // palette no longer uses green, so there's no collision.)
        const isBest = spec.showBest !== false && best === i;
        const isWorst = spec.showWorst !== false && worst === i;
        const cellBg = isBest
          ? "bg-emerald-50"
          : isWorst ? "bg-rose-50" : "";
        return (
          <td
            key={i}
            className={`text-sm text-gray-900 py-3 px-4 align-top whitespace-nowrap ${cellBg}`}
            // The cell tint (emerald-50 / rose-50 swap to dark equivalents
            // in dark mode) already conveys best/worst - the redundant
            // colored dot was visual noise, removed per user feedback.
            aria-label={isBest ? t("compare.table.best") : isWorst ? t("compare.table.worst") : undefined}
          >
            {d}
          </td>
        );
      })}
    </tr>
  );
}

function BoolRow({ spec, t }: { spec: BoolRowSpec; t: ReturnType<typeof useT> }) {
  // All-unknown row: every cell would read "N/A" - hide it.
  if (spec.values.every((v) => v == null)) return null;
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <th scope="row" className="sticky left-0 z-10 bg-white text-xs font-medium text-gray-500 text-left py-3 pl-4 pr-4 whitespace-nowrap align-top">
        {t(spec.labelKey)}
      </th>
      {spec.values.map((v, i) => (
        <td key={i} className="text-sm text-gray-900 py-3 px-4 align-top whitespace-nowrap">
          {v === true
            ? t("compare.table.yes")
            : v === false
              ? t("compare.table.no")
              : t("compare.table.na")}
        </td>
      ))}
    </tr>
  );
}

function TextRow({
  labelKey, values, t,
}: { labelKey: TranslationKey; values: React.ReactNode[]; t: ReturnType<typeof useT> }) {
  // Hide the row when NO listing has a value (every cell would be "N/A").
  if (values.every((v) => isEmptyCell(v, t("compare.table.na")))) return null;
  return (
    <tr className="border-b border-gray-100 last:border-0">
      <th scope="row" className="sticky left-0 z-10 bg-white text-xs font-medium text-gray-500 text-left py-3 pl-4 pr-4 whitespace-nowrap align-top">
        {t(labelKey)}
      </th>
      {values.map((v, i) => (
        <td key={i} className="text-sm text-gray-900 py-3 px-4 align-top">
          {v || t("compare.table.na")}
        </td>
      ))}
    </tr>
  );
}

// Fixed column widths (px). Pinning these in pixels (instead of the
// previous `calc((100% - 160px) / N)` percentage math) makes columns
// line up across sections at every viewport - the percentage approach
// drifted when container widths weren't identical (mobile especially,
// where each section had its own overflow-x-auto). With fixed pixel
// widths the table naturally overflows on narrow viewports; the
// sticky first column keeps labels pinned so horizontal scrolling
// stays legible.
const LABEL_COL_PX = 120;
const DATA_COL_PX = 180;

// Collapsible sub-section. Each section (Price / Specs / Location /
// Risks) renders as a small accordion card inside the master
// "Detailed comparison" card. Defaults open; the user can close
// individual groups for less density. The thead inside the body shows
// the section title in the sticky first cell alongside the listing
// badges - so when expanded the top row reads "Price | Elan 1 | Elan 2"
// rather than having a blank cell above the label column.
function Section({
  titleKey, defaultOpen = true, children, colCount, headerRow,
}: {
  titleKey: TranslationKey;
  defaultOpen?: boolean;
  children: React.ReactNode;
  colCount: number;
  headerRow?: React.ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Toggle bar - click anywhere on the row to flip open/closed.
          Title sits next to a chevron that rotates 180° on open. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-gray-900">
          {t(titleKey)}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {/* grid-template-rows 0fr→1fr animation - the cell needs
          `min-h-0 overflow-hidden` so the grid can collapse to 0. */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
        aria-hidden={!open}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table
              className="w-full"
              style={{
                tableLayout: "fixed",
                minWidth: `${LABEL_COL_PX + DATA_COL_PX * colCount}px`,
              }}
            >
              <colgroup>
                <col style={{ width: `${LABEL_COL_PX}px` }} />
                {Array.from({ length: colCount }).map((_, i) => (
                  <col key={i} style={{ width: `${DATA_COL_PX}px` }} />
                ))}
              </colgroup>
              {headerRow && <thead>{headerRow}</thead>}
              <tbody>{children}</tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ComparisonTable({ results }: Props) {
  const t = useT();
  const { locale } = useLocale();
  if (results.length === 0) return null;

  // Rent listings are always priced as monthly totals; the frontend
  // rent/sale mismatch guard already enforces that all listings in a
  // single comparison share a mode, so looking at the first row is
  // enough. Default to "sale" for legacy rows that never stamped an
  // analysis_mode field.
  const isRent = results[0]?.analysis_mode === "rent";
  const priceUnit = isRent ? t("result.permonth") : "AZN";
  // Localized missing-value placeholder. Rendered in cells AND treated
  // as "empty" by isEmptyCell() so all-missing rows stay suppressed.
  const na = t("compare.table.na");
  const orNa = (s: string) => (s === "-" ? na : s);
  // Band rounding grain - rent bands live in the 100s of AZN/mo, so the
  // sale-friendly 1000 grain collapses both bounds to one number. Matches
  // VerdictGauge / the compare cards.
  const bandGrain = isRent ? 10 : 1000;
  const roundBand = (n: number) => Math.round(n / bandGrain) * bandGrain;

  // Confidence-tint classes for the badge under the fair-value cell.
  const confTintClass = (level: "high" | "medium" | "low"): string =>
    level === "high"
      ? "bg-emerald-50 text-emerald-700"
      : level === "medium"
        ? "bg-amber-50 text-amber-700"
        : "bg-rose-50 text-rose-700";

  // Column-header row - listing badges aligned above each data column.
  // The sticky first cell stays empty on purpose now: the section title
  // already appears in the Section's toggle bar ABOVE the table, so
  // repeating it here duplicates text. Using a non-breaking space keeps
  // the row height stable while still letting the sticky column paint
  // its bg-white strip over scrolling columns.
  const headerRow = (
    <tr className="border-b border-gray-100">
      <th
        aria-hidden
        className="sticky left-0 z-10 bg-white py-3 pl-4 pr-4"
      />
      {results.map((_, i) => {
        const c = colorForIndex(i);
        return (
          <th key={i} className="px-4 py-3 text-left">
            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${c.label}`}>
              <span className={`w-2 h-2 rounded-full ${c.dot}`} aria-hidden />
              {t("compare.cards.listing_label").replace("{n}", String(i + 1))}
            </span>
          </th>
        );
      })}
    </tr>
  );

  return (
    // Hidden on phones - CompareBoard's mobile layout takes over. The
    // table's fixed 180px data columns mean only one listing's column
    // fits a 360px viewport at a time, defeating the compare premise
    // unless the user discovers the horizontal scroll most don't.
    <section className="rounded-2xl border border-gray-200 bg-white overflow-hidden hidden sm:block">
      {/* Master header - always visible, non-collapsible. Same squircle
          + plain-semibold treatment as the other result cards. Users
          collapse individual sub-sections (Price / Specs / Location /
          Risks) instead; the master stays open. */}
      <div className="flex items-center gap-3 p-5 border-b border-gray-100">
        <div className="shrink-0 w-9 h-9 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center">
          <Columns3 className="w-4.5 h-4.5 text-gray-600" />
        </div>
        <span className="text-sm font-semibold text-gray-900 truncate">
          {t("compare.table.title")}
        </span>
      </div>
      <div className="p-5 space-y-3">
      <Section
        titleKey="compare.table.price_section"
        colCount={results.length}
        headerRow={headerRow}
      >
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.asking_price",
            values: results.map((r) => r.price),
            display: results.map((r) => `${formatPrice(r.price)} ${priceUnit}`),
            lowerIsBetter: true,
            // Asking price is informational - cheap != good, expensive != bad.
            // The deal/overpriced signal lives on the rows below.
            showBest: false,
            showWorst: false,
          }}
        />
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.fair_value",
            values: results.map((r) => r.adjusted_predicted_price ?? r.predicted_price),
            // Multi-line cell: point estimate on top, then the model's
            // predicted RANGE and a high/medium/low confidence badge.
            // Showing the band kills the false precision of a lone point,
            // and the badge lets a buyer weight a tight high-confidence
            // call over a wide shaky one (which the verdict chip also
            // dims on the cards).
            display: results.map((r) => {
              const v = r.adjusted_predicted_price ?? r.predicted_price;
              if (!v) return na;
              const band = resolveBand(r);
              return (
                <span className="flex flex-col leading-tight gap-0.5">
                  <span>{formatPrice(roundEstimate(v, isRent))} {priceUnit}</span>
                  {band && (
                    <span className="text-[11px] text-gray-500 font-normal">
                      {formatBand(band, formatPrice, roundBand)}
                    </span>
                  )}
                  {band && band.confidencePct != null && (
                    <span
                      className={`mt-0.5 inline-flex w-fit items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${confTintClass(band.level)}`}
                    >
                      {band.confidencePct}% {t(`confidence.label.${band.level}` as TranslationKey)}
                    </span>
                  )}
                </span>
              );
            }),
            lowerIsBetter: false,
            // Fair value is the AI's estimate for that listing - not a
            // ranking. "Highest predicted value wins" is nonsense to a
            // buyer. The actual deal signal is on Overpriced % / Savings.
            showBest: false,
            showWorst: false,
          }}
        />
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.overpriced_pct",
            values: results.map((r) => r.overpriced_pct),
            display: results.map((r) => orNa(fmtPct(r.overpriced_pct))),
            lowerIsBetter: true,  // most-underpriced wins
            showWorst: true,
          }}
        />
        {/* Government second-opinion row. Only renders when at least
            one listing has a notariat estimate - otherwise the row
            is dead weight (every cell would read "-"). The companion
            "vs notariat" delta sits alongside our overpriced %, so
            the buyer can sanity-check our deal call against the
            transaction-trained model from notariat.az. */}
        {results.some((r) => r.notariat_estimate?.total_price != null) && (
          <>
            <NumericRow
              t={t}
              spec={{
                labelKey: "compare.table.row.notariat",
                values: results.map((r) => r.notariat_estimate?.total_price ?? null),
                display: results.map((r) => {
                  const v = r.notariat_estimate?.total_price;
                  return v ? `${formatPrice(v)} ${priceUnit}` : na;
                }),
                lowerIsBetter: false,
                // Government estimate is informational, not a ranking.
                showBest: false,
                showWorst: false,
              }}
            />
            <NumericRow
              t={t}
              spec={{
                labelKey: "compare.table.row.notariat_delta",
                values: results.map((r) => {
                  const nv = r.notariat_estimate?.total_price;
                  const ask = r.price;
                  if (!nv || !ask) return null;
                  return ((ask - nv) / nv) * 100;
                }),
                display: results.map((r) => {
                  const nv = r.notariat_estimate?.total_price;
                  const ask = r.price;
                  if (!nv || !ask) return na;
                  return fmtPct(((ask - nv) / nv) * 100);
                }),
                lowerIsBetter: true, // most below the notariat number wins
                showWorst: true,
              }}
            />
          </>
        )}
        {/* Deal-vs-own-complex row. Only renders when at least one
            listing matched a catalog residence (otherwise every cell is
            "-"). The most decisive same-building signal: how far the
            unit's own AZN/m² sits under/over the typical AZN/m² for the
            matched residence's same room/floor/repair segment. Negative
            (under the building) is the better deal, so lowerIsBetter. */}
        {results.some((r) => ownComplexDelta(r) != null) && (
          <NumericRow
            t={t}
            spec={{
              labelKey: "compare.table.row.vs_own_complex",
              values: results.map((r) => ownComplexDelta(r)?.pct ?? null),
              display: results.map((r) => {
                const own = ownComplexDelta(r);
                if (!own) return na;
                const ownNeg = own.pct < -0.5;
                const ownPos = own.pct > 0.5;
                const tone = ownNeg
                  ? "text-emerald-700"
                  : ownPos ? "text-amber-700" : "text-gray-600";
                return (
                  <span className="flex flex-col leading-tight">
                    <span className={`font-semibold ${tone}`}>{fmtPct(own.pct)}</span>
                    <span className="text-[11px] text-gray-500 font-normal truncate">
                      {own.name}
                    </span>
                  </span>
                );
              }),
              lowerIsBetter: true, // most under the building's typical wins
              showWorst: true,
            }}
          />
        )}
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.savings",
            values: results.map((r) => r.savings),
            display: results.map((r) =>
              r.savings > 0 ? `${formatPrice(r.savings)} ${priceUnit}` : na,
            ),
            lowerIsBetter: false,
            showWorst: false,
          }}
        />
        <TextRow
          t={t}
          labelKey="compare.table.row.verdict"
          values={results.map((r) => translateVerdict(r.verdict, locale))}
        />
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.ppm",
            values: results.map((r) => pricePerM2(r)),
            display: results.map((r) => {
              const ppm = pricePerM2(r);
              return ppm ? `${Math.round(ppm).toLocaleString()} AZN/m²` : na;
            }),
            lowerIsBetter: true,
            showWorst: false,
          }}
        />
      </Section>

      {/* Property specs */}
      <Section
        titleKey="compare.table.specs_section"
        colCount={results.length}
        headerRow={headerRow}
      >
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.area",
            values: results.map((r) => r.area_m2),
            display: results.map((r) => orNa(fmtNum(r.area_m2, "m²"))),
            lowerIsBetter: false,
            showWorst: false,
          }}
        />
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.rooms",
            values: results.map((r) => r.room_count),
            display: results.map((r) => orNa(fmtNum(r.room_count))),
            lowerIsBetter: false,
            showWorst: false,
          }}
        />
        <TextRow
          t={t}
          labelKey="compare.table.row.floor"
          values={results.map((r) =>
            r.floor != null && r.total_floors != null
              ? `${r.floor} / ${r.total_floors}`
              : null,
          )}
        />
        <TextRow
          t={t}
          labelKey="compare.table.row.category"
          values={results.map((r) => r.category_name || null)}
        />
        <BoolRow t={t} spec={{ labelKey: "compare.table.row.repair", values: results.map((r) => r.has_repair) }} />
        <BoolRow t={t} spec={{ labelKey: "compare.table.row.kupcha", values: results.map((r) => r.has_bill_of_sale) }} />
      </Section>

      {/* Location */}
      <Section
        titleKey="compare.table.location_section"
        colCount={results.length}
        headerRow={headerRow}
      >
        <TextRow
          t={t}
          labelKey="compare.table.row.district"
          values={results.map((r) => r.location_name || null)}
        />
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.metro",
            values: results.map((r) => cf(r, "dist_to_nearest_metro_km")),
            // Two-line cell: distance on top, station name below. Gives
            // the buyer the raw distance signal first, with the station
            // name as context. Previously the inline "0.4 km · 28 May"
            // made both pieces compete for scan time.
            display: results.map((r) => {
              const km = fmtKm(cf(r, "dist_to_nearest_metro_km"));
              const nearest = r.context?.nearby_metros?.[0]?.name;
              if (km === "-") return na;
              return (
                <span className="flex flex-col leading-tight">
                  <span>{km}</span>
                  {nearest && (
                    <span className="text-[11px] text-gray-500 font-normal">
                      {nearest}
                    </span>
                  )}
                </span>
              );
            }),
            lowerIsBetter: true,
            showWorst: false,
          }}
        />
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.center",
            values: results.map((r) => cf(r, "dist_to_center_km")),
            display: results.map((r) => orNa(fmtKm(cf(r, "dist_to_center_km")))),
            lowerIsBetter: true,
            showWorst: false,
          }}
        />
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.sea",
            values: results.map((r) => cf(r, "dist_to_sea_km")),
            display: results.map((r) => orNa(fmtKm(cf(r, "dist_to_sea_km")))),
            lowerIsBetter: true,
            showWorst: false,
          }}
        />
        {/* District typical price-per-m² used to live under its own
            "Market Context" section - collapsed by default, with a
            single row inside. That was overkill (and the "context"
            wording read as jargon). Merged here since it is, plainly,
            a location attribute. */}
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.district_median_ppm",
            values: results.map((r) => r.context?.location_typical_ppm ?? null),
            display: results.map((r) => {
              const v = r.context?.location_typical_ppm;
              return typeof v === "number"
                ? `${Math.round(v).toLocaleString()} AZN/m²`
                : na;
            }),
            lowerIsBetter: true,
            showWorst: false,
          }}
        />
      </Section>

      {/* Red flags - numeric overview only. The long-text findings
          list used to live in this section as a squeeze-into-a-cell
          row; lifted it out into its own cards-based grid below
          (see RisksByListing) where each finding has real width. */}
      <Section
        titleKey="compare.table.flags_section"
        colCount={results.length}
        headerRow={headerRow}
      >
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.red_flag_count",
            values: results.map((r) => redFlagCount(r)),
            display: results.map((r) => String(redFlagCount(r))),
            lowerIsBetter: true,
            showWorst: true,
          }}
        />
        <NumericRow
          t={t}
          spec={{
            labelKey: "compare.table.row.criticality",
            values: results.map((r) => r.criticality),
            display: results.map((r) =>
              typeof r.criticality === "number" ? `${r.criticality} / 5` : na,
            ),
            // Higher criticality = more verification needed = worse
            // buyer-facing signal.
            lowerIsBetter: true,
            showWorst: true,
          }}
        />
      </Section>

      {/* Per-listing risk findings - cards instead of a table row so
          a 120-char "Title deed missing" warning doesn't squeeze into
          a 180-px cell and wrap 4 lines tall. Full width lets each
          finding breathe, and mobile stacking stays readable. */}
      <RisksByListing results={results} />
      </div>
    </section>
  );
}

// Per-listing risks rendered as a card grid. Lives OUTSIDE the
// comparison table because fact-check findings are full sentences
// (often 80–120 chars) and squeezing them into a table cell wrapped
// them into 3–4 lines of unreadable slop. A card grid gives each
// listing a proper column width at every viewport size.
//
// Grid cadence mirrors the other compare grids:
//   • <sm: 1 col (stack)
//   • sm:  2 cols
//   • lg:  3 cols (or 2, when there are only 2 listings)
//   • xl:  up to 4 cols
//
// Listings with no critical / mismatch / warning findings still show
// their card with an "-" placeholder, so the grid stays aligned with
// the headline card row above. Compact, predictable rhythm.
export function RisksByListing({ results }: { results: AnalysisResult[] }) {
  const t = useT();
  if (results.length === 0) return null;
  // Nothing to warn about anywhere: a grid of "-" cards reads as a
  // broken section, not as good news. The Risks table rows above
  // already show the zero counts.
  const anyFindings = results.some((r) =>
    (r.fact_checks || []).some(
      (f) => f.status === "critical" || f.status === "mismatch" || f.status === "warning",
    ),
  );
  if (!anyFindings) return null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">
        {t("compare.table.risks_section")}
      </h3>
      {/* Capped at 2-per-row regardless of listing count. Risk
          findings are long prose (often 80–120 chars each), and
          squeezing them into 3 or 4 narrow columns made the text
          wrap awkwardly. Two wide columns give each finding a full
          readable width at every desktop size; mobile still stacks
          single-column below sm. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {results.map((r, i) => {
          const c = colorForIndex(i);
          // Our deed-claim finding in our own words (lib/deed-claim).
          const findings = inOwnWords(t, r.fact_checks).filter((f) =>
            f.status === "critical" || f.status === "mismatch" || f.status === "warning",
          );
          return (
            <div key={i} className="min-w-0 space-y-2">
              {/* Header - matches the column-header badge style used
                  at the top of the table, so users can link each card
                  back to its listing colour visually. */}
              <div className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${c.label}`}>
                <span className={`w-2 h-2 rounded-full ${c.dot}`} aria-hidden />
                {t("compare.cards.listing_label").replace("{n}", String(i + 1))}
              </div>
              {findings.length === 0 ? (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
                  {t("compare.table.no_risks")}
                </div>
              ) : (
                <ul className="space-y-2">
                  {/* Same anatomy as the result page's findings: an icon
                      in the semantic colour and plain text, no tinted
                      block per line. */}
                  {findings.slice(0, 5).map((f, j) => {
                    const critical = f.status === "critical" || f.status === "mismatch";
                    return (
                      <li key={j} className="flex items-start gap-2 text-xs leading-snug text-gray-700 break-words">
                        <AlertTriangle
                          className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${critical ? "text-rose-500" : "text-amber-500"}`}
                          aria-hidden
                        />
                        <span>{f.message}</span>
                      </li>
                    );
                  })}
                  {findings.length > 5 && (
                    <li className="text-[10px] text-gray-400">
                      +{findings.length - 5} {t("compare.table.row.risks_more")}
                    </li>
                  )}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
